import { CAZA } from '@somnhold/content';
import { hexLen, hexToWorld, ROW, worldToHex, type Hex } from '../hex.ts';
import { cosA, iatan2, idiv, imax, imin, isqrt, sinA } from '../math/fixed.ts';
import { deriveSeed, rngInt, rngRange, seedRng, type RngState } from '../math/rng.ts';
import type { Pt, TrailBranch, TrailCrossing } from '../caza-types.ts';

/**
 * The Long Hunt's grounds from the seed (docs/sendas/barbaro.md, chapter 1): the corral (act 1), the pastures' tracks
 * (act 2) and the ledge over the sea (act 3). The fight's builder (campaign/sendas.ts) and the renderer build them the
 * same way. Integer maths only.
 */

export const PENS_RADIUS = 9;
/** The corral's wall: a ring of stones at this many hexes from the middle. */
export const PENS_WALL = 6;
export const MOOR_RADIUS = 11;
export const BRINK_RADIUS = 15;

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};

const dist = (a: Pt, b: Pt): number => isqrt((a.x - b.x) * (a.x - b.x) + (a.y - b.y) * (a.y - b.y));

/* ---------- the corral ---------- */

export interface PensLayout {
  /** The board's rocks: the wall (its gate open) and everything past the mud round it. */
  rocks: Hex[];
  wall: Hex[];
  gate: Hex[];
  /** Each boar's spot: a huddle against the far wall (north, −y). */
  homes: Pt[];
  rim: Pt[];
  wallAt: Pt[];
  gateAt: Pt;
  grayIn: Pt;
  grayOut: Pt;
  start: Pt;
  /** Fires and torches round it (for the drawing): inside by the wall, and the camp's outside. */
  fires: Pt[];
}

export const pensLayout = (seed: number): PensLayout => {
  const rng = seedRng(deriveSeed(seed, 0xb0a7));
  const W = PENS_WALL;
  // The gate: two stones of the wall at the south (toward the camera), one either side of the middle line.
  const gate: Hex[] = [
    { q: -3, r: W },
    { q: -2, r: W },
  ];
  const isGate = (h: Hex): boolean => gate.some((g) => g.q === h.q && g.r === h.r);
  const wall: Hex[] = [];
  const rocks: Hex[] = [];
  for (const h of allCells(PENS_RADIUS + 2)) {
    const d = hexLen(h.q, h.r);
    if (d === W && !isGate(h)) {
      wall.push(h);
      rocks.push(h);
    } else if (d >= W + 3) rocks.push(h);
  }
  // The herd against the far wall: twelve in a loose huddle round a point three hexes north of the middle.
  const cx = rngRange(rng, -6, 6) * 100;
  const cy = -3 * ROW - 300;
  const homes: Pt[] = [];
  const ring = [
    [0, 0],
    [700, 0],
    [-700, 0],
    [350, 600],
    [-350, 600],
    [350, -600],
    [-350, -600],
    [1050, 600],
    [-1050, 600],
    [1400, 0],
    [-1400, 0],
    [0, 1200],
  ] as const;
  for (const [dx, dy] of ring) homes.push({ x: cx + dx + rngRange(rng, -90, 90), y: cy + dy + rngRange(rng, -80, 80) });
  // The bands come over the wall from all round, a little more from the far side (the dark).
  const rim: Pt[] = [];
  for (const h of allCells(W + 2)) if (hexLen(h.q, h.r) === W + 2 && h.r < W) rim.push(hexToWorld(h.q, h.r));
  rim.sort((a, b) => a.y - b.y || a.x - b.x);
  const wallAt = wall.map((h) => hexToWorld(h.q, h.r));
  const gateAt = { x: idiv(hexToWorld(-3, W).x + hexToWorld(-2, W).x, 2), y: W * ROW };
  // The Grey Wolf over the north-west of the wall, out by the north-east.
  const side = rngInt(rng, 2) === 0 ? -1 : 1;
  const grayIn = hexToWorld(side * 4, -(W + 1));
  const grayOut = hexToWorld(-side * 2 + (side < 0 ? 5 : -5), -(W + 1));
  const fires: Pt[] = [hexToWorld(-4, -1), hexToWorld(5, -2), hexToWorld(4, 1), hexToWorld(-5, 3), hexToWorld(-6, W + 1), hexToWorld(1, W + 1), hexToWorld(8, -4), hexToWorld(-8, 2)];
  return { rocks, wall, gate, homes, rim, wallAt, gateAt, grayIn, grayOut, start: { x: 0, y: 2 * ROW }, fires };
};

/* ---------- the tracks ---------- */

export interface TrailLayout {
  kind: 'tracks' | 'brink';
  radius: number;
  rocks: Hex[];
  /** The ledge's two sides (act 3): the cliff's foot (west) and the drop's edge (east), x by row (r from −R to R). */
  west: number[];
  east: number[];
  crossings: TrailCrossing[];
  end: Pt;
  start: Pt;
  /** The ledge's loose stones (act 3), on the cliff's side of the way. */
  stones: Pt[];
  /** Puddles and pools besides the clue ones (the drawing's). */
  puddles: Pt[];
}

/** Points every `step` along a polyline through `ctl` (its first and last kept). */
const resample = (ctl: Pt[], step: number): Pt[] => {
  const out: Pt[] = [{ ...(ctl[0] as Pt) }];
  let carry = 0;
  for (let i = 0; i + 1 < ctl.length; i++) {
    const a = ctl[i] as Pt;
    const b = ctl[i + 1] as Pt;
    const d = dist(a, b);
    if (d === 0) continue;
    let t = step - carry;
    while (t <= d) {
      out.push({ x: a.x + idiv((b.x - a.x) * t, d), y: a.y + idiv((b.y - a.y) * t, d) });
      t += step;
    }
    carry = d - (t - step);
  }
  const last = ctl[ctl.length - 1] as Pt;
  const tail = out[out.length - 1] as Pt;
  if (dist(tail, last) > idiv(step, 3)) out.push({ ...last });
  else out[out.length - 1] = { ...last };
  return out;
};

/** A way from `a` toward an angle `ang` (1024 steps), `len` long, bending `bend` sideways at its middle. */
const wayOut = (a: Pt, ang: number, len: number, bend: number): Pt[] => {
  const ex = a.x + idiv(cosA(ang) * len, 10000);
  const ey = a.y + idiv(sinA(ang) * len, 10000);
  const nx = -idiv(sinA(ang) * bend, 10000);
  const ny = idiv(cosA(ang) * bend, 10000);
  const m1 = { x: a.x + idiv(ex - a.x, 3) + idiv(nx, 2), y: a.y + idiv(ey - a.y, 3) + idiv(ny, 2) };
  const m2 = { x: a.x + idiv((ex - a.x) * 2, 3) + nx, y: a.y + idiv((ey - a.y) * 2, 3) + ny };
  return resample([a, m1, m2, { x: ex, y: ey }], 450);
};

/** A way from `a` to `b`, bending `bend` sideways. */
const wayTo = (a: Pt, b: Pt, bend: number): Pt[] => {
  const d = imax(1, dist(a, b));
  const nx = -idiv((b.y - a.y) * bend, d);
  const ny = idiv((b.x - a.x) * bend, d);
  const m1 = { x: a.x + idiv(b.x - a.x, 3) + idiv(nx, 2), y: a.y + idiv(b.y - a.y, 3) + idiv(ny, 2) };
  const m2 = { x: a.x + idiv((b.x - a.x) * 2, 3) + nx, y: a.y + idiv((b.y - a.y) * 2, 3) + ny };
  return resample([a, m1, m2, b], 450);
};

/** The way's index at about this fraction of it (bp). */
const at = (pts: Pt[], bp: number): number => imax(1, imin(pts.length - 2, idiv((pts.length - 1) * bp, 10000)));

/** What a crossing shows (act 2 teaches one clue a crossing; act 3 mixes them). */
type Plan = { n: number; stream: 'all' | 'some' | 'none'; pit: boolean; late: boolean; packFar?: boolean };

/** Whether a point is on the ground (inside the board, on the ledge for act 3). */
const onGround = (L: { kind: string; radius: number; west: number[]; east: number[] }, p: Pt): boolean => {
  const R = L.radius;
  const h = worldToHex(p.x, p.y);
  const r = h.r;
  if (r < -(R - 1) || r > R - 1) return false;
  if (L.kind === 'tracks') return hexLen(h.q, r) <= R - 1;
  const w = L.west[r + R] as number;
  const e = L.east[r + R] as number;
  return p.x > w + 700 && p.x < e - 700;
};

/** Every point of a way on the ground. */
const allOn = (L: Parameters<typeof onGround>[0], pts: Pt[]): boolean => pts.every((p) => onGround(L, p));

/** The nearest two points of two ways, past `skip` of each from where they start. */
const apart = (a: Pt[], b: Pt[], skip: number): number => {
  let best = 0x7fffffff;
  for (let i = skip; i < a.length; i++) for (let j = skip; j < b.length; j++) best = imin(best, dist(a[i] as Pt, b[j] as Pt));
  return best;
};

/**
 * One crossing: the Wolf's branch from `a` to `b`, and `n − 1` of the pack's fanning off it, each long as the Wolf's;
 * its clues by the plan. Null if the seed's draw doesn't fit (the caller draws again).
 */
const crossing = (rng: RngState, L: Parameters<typeof onGround>[0], a: Pt, b: Pt, plan: Plan, see: number): TrailCrossing | null => {
  const right = wayTo(a, b, rngRange(rng, -1200, 1200));
  if (!allOn(L, right)) return null;
  const len = dist(a, b) + 300;
  const base = iatan2(b.y - a.y, b.x - a.x);
  const ways: Pt[][] = [right];
  // The pack's: off to either side, never too near the Wolf's nor each other past their start.
  for (let m = 1; m < plan.n; m++) {
    let ok: Pt[] | null = null;
    for (let k = 0; k < 14 && !ok; k++) {
      const sd = rngInt(rng, 2) === 0 ? -1 : 1;
      const w = wayOut(a, base + sd * rngRange(rng, 85, 200), len + rngRange(rng, -300, 300), rngRange(rng, -900, 900));
      if (!allOn(L, w)) continue;
      if (ways.some((o) => apart(o, w, 3) < 1500)) continue;
      if (dist(w[w.length - 1] as Pt, b) < 2600) continue;
      ok = w;
    }
    if (!ok) return null;
    ways.push(ok);
  }
  // Shuffle where the Wolf's sits among them (it is never always the middle one).
  const order = ways.map((_, i) => i);
  for (let i = order.length - 1; i > 0; i--) {
    const j = rngInt(rng, i + 1);
    const t = order[i] as number;
    order[i] = order[j] as number;
    order[j] = t;
  }
  // Near (within sight of the middle, a stretch in) or past it (the first stretch tells nothing).
  const nearBp = (): number => rngRange(rng, 2600, 4600);
  const farBp = (pts: Pt[]): number => {
    // Past `see` from the middle (and a little more), before the end.
    for (let i = 1; i < pts.length - 1; i++) if (dist(pts[i] as Pt, a) > see + 450) return imax(idiv(i * 10000, pts.length - 1), rngRange(rng, 7000, 8200));
    return 8000;
  };
  // How trodden each is: the Wolf's alone (1); the pack's first two or three, any other one to three (the most trodden
  // is always the pack's, the faintest not always the Wolf's).
  const branches: TrailBranch[] = ways.map((pts, i) => ({ pts, right: i === 0, stream: -1, jump: false, puddle: -1, drink: false, pit: -1, marks: i === 0 ? 1 : i === 1 ? rngRange(rng, 2, 3) : rngRange(rng, 1, 3) }));
  const streamBp = plan.late ? 0 : nearBp();
  // The stream: across all the branches, or the Wolf's and one of the pack's.
  const streamed = plan.stream === 'all' ? branches.map((_, i) => i) : plan.stream === 'some' ? [0, 1] : [];
  for (const i of streamed) {
    const br = branches[i] as TrailBranch;
    br.stream = at(br.pts, plan.late ? farBp(br.pts) : streamBp + rngRange(rng, -400, 400));
    br.jump = !br.right;
  }
  // Every one of the pack's not jumping a stream drinks at a puddle; the Wolf's passes one by, now and then.
  for (const br of branches) {
    if (br.right) {
      if (rngInt(rng, 2) === 0) br.puddle = at(br.pts, plan.late ? farBp(br.pts) : nearBp());
      if (plan.pit) br.pit = at(br.pts, plan.late ? farBp(br.pts) : rngRange(rng, 2000, 3600));
      if (br.pit >= 0 && br.pit === br.stream) br.pit = imin(br.pts.length - 2, br.pit + 2);
      if (br.puddle >= 0 && (Math.abs(br.puddle - br.stream) < 2 || Math.abs(br.puddle - br.pit) < 2)) br.puddle = -1;
      continue;
    }
    if (!br.jump) {
      br.drink = true;
      br.puddle = at(br.pts, plan.late || plan.packFar ? farBp(br.pts) : nearBp());
      if (Math.abs(br.puddle - br.stream) < 2) br.puddle = imin(br.pts.length - 2, br.puddle + 2);
    }
  }
  // The stream's line, bank to bank: through where the branches cross it, a hex past the outer two.
  const stream: Pt[] = [];
  if (streamed.length) {
    const cross = streamed.map((i) => (branches[i] as TrailBranch).pts[(branches[i] as TrailBranch).stream] as Pt);
    cross.sort((p, q) => iatan2(p.y - a.y, p.x - a.x) - iatan2(q.y - a.y, q.x - a.x));
    const f = cross[0] as Pt;
    const l = cross[cross.length - 1] as Pt;
    const dx = l.x - f.x;
    const dy = l.y - f.y;
    const d = imax(1, isqrt(dx * dx + dy * dy));
    const ext = streamed.length === 1 ? 1600 : 1300;
    const ux = streamed.length === 1 ? -idiv(((right[1] as Pt).y - a.y) * 10000, imax(1, dist(right[1] as Pt, a))) : idiv(dx * 10000, d);
    const uy = streamed.length === 1 ? idiv(((right[1] as Pt).x - a.x) * 10000, imax(1, dist(right[1] as Pt, a))) : idiv(dy * 10000, d);
    stream.push({ x: f.x - idiv(ux * ext, 10000), y: f.y - idiv(uy * ext, 10000) }, ...cross, { x: l.x + idiv(ux * ext, 10000), y: l.y + idiv(uy * ext, 10000) });
  }
  // Reorder so the branches lie in the shuffled order (the right one's index with them).
  const out = order.map((i) => branches[i] as TrailBranch);
  return { at: { ...a }, branches: out, right: out.findIndex((x) => x.right), stream, late: plan.late };
};

/** What each crossing shows: act 2 one clue a crossing (water, weight, drinking, then all three), act 3 mixed, three late. */
const plans = (rng: RngState, kind: 'tracks' | 'brink', n: number): Plan[] => {
  if (kind === 'tracks')
    return [
      { n: 2, stream: 'all', pit: false, late: false },
      { n: 2, stream: 'none', pit: true, late: false, packFar: true },
      { n: 2, stream: 'none', pit: false, late: false },
      { n: 3, stream: 'some', pit: rngInt(rng, 2) === 0, late: false },
    ];
  // Three of the seven late, never the first; the first of two branches, the rest of three.
  const late = new Set<number>();
  while (late.size < 3) late.add(1 + rngInt(rng, n - 1));
  const out: Plan[] = [];
  for (let i = 0; i < n; i++) {
    const k = rngInt(rng, 3);
    out.push({ n: i === 0 ? 2 : 3, stream: k === 0 ? 'all' : k === 1 ? 'some' : 'none', pit: rngInt(rng, 10) < 5, late: late.has(i) });
  }
  return out;
};

/** The ledge's two sides by row (act 3): a shelf about eleven hexes wide, the cliff west, the drop east, winding. */
const shelf = (rng: RngState, R: number): { west: number[]; east: number[] } => {
  const west: number[] = [];
  const east: number[] = [];
  let mid = 0;
  let dir = rngInt(rng, 2) === 0 ? -1 : 1;
  for (let r = -R; r <= R; r++) {
    if (rngInt(rng, 10) < 3) dir = -dir;
    mid = imax(-2500, imin(2500, mid + dir * 250));
    const half = 5600 + rngRange(rng, -250, 250);
    west.push(mid - half);
    east.push(mid + half);
  }
  return { west, east };
};

/**
 * The tracks from the seed: `kind` 'tracks' (act 2, the pastures: four crossings) or 'brink' (act 3, the ledge over the
 * sea: seven). The crossings go north (−y) from the foot of the board; each crossing's Wolf's branch ends where the
 * next begins. Drawn again (a new salt) until every branch fits on the ground and apart from the others.
 */
export const trailLayout = (seed: number, kind: 'tracks' | 'brink'): TrailLayout => {
  const R = kind === 'tracks' ? MOOR_RADIUS : BRINK_RADIUS;
  const n = kind === 'tracks' ? CAZA.trail.tracks.crossings : CAZA.trail.brink.crossings;
  const see = CAZA.trail.see;
  for (let salt = 0; ; salt++) {
    const rng = seedRng(deriveSeed(seed, 0x7a11 + salt * 31 + (kind === 'brink' ? 977 : 0)));
    const { west, east } = kind === 'brink' ? shelf(rng, R) : { west: [] as number[], east: [] as number[] };
    const ground = { kind, radius: R, west, east };
    const top = -(R - 2) * ROW;
    const foot = (R - 2) * ROW;
    const step = idiv(foot - top, n);
    const nodes: Pt[] = [];
    // On the ledge the way zigzags from side to side (its stretches longer than the sight of three hexes).
    const zig = rngInt(rng, 2) === 0 ? -1 : 1;
    for (let i = 0; i <= n; i++) {
      const y = foot - i * step;
      const r = worldToHex(0, y).r;
      const c = kind === 'brink' ? idiv((west[r + R] as number) + (east[r + R] as number), 2) : 0;
      const x = kind === 'brink' ? c + (i % 2 ? zig : -zig) * rngRange(rng, 1300, 2300) : rngRange(rng, -3200, 3200);
      nodes.push({ x, y: y + rngRange(rng, -250, 250) });
    }
    const ps = plans(rng, kind, n);
    const crossings: TrailCrossing[] = [];
    for (let i = 0; i < n; i++) {
      let c: TrailCrossing | null = null;
      for (let k = 0; k < 6 && !c; k++) c = crossing(rng, ground, nodes[i] as Pt, nodes[i + 1] as Pt, ps[i] as Plan, see);
      if (!c) break;
      crossings.push(c);
    }
    if (crossings.length < n || (salt < 200 && !trailCheck({ crossings, see }))) continue;
    const rocks: Hex[] = [];
    for (const h of allCells(R + 2)) {
      const p = hexToWorld(h.q, h.r);
      if (kind === 'tracks') {
        if (hexLen(h.q, h.r) > R) rocks.push(h);
        continue;
      }
      if (h.r < -R || h.r > R || !(p.x > (west[h.r + R] as number) && p.x < (east[h.r + R] as number)) || hexLen(h.q, h.r) > R) rocks.push(h);
    }
    // The ledge's loose stones: one by each crossing past the first, a hex in from the cliff's foot.
    const stones: Pt[] = [];
    if (kind === 'brink')
      for (let i = 1; i < n; i++) {
        const y = (nodes[i] as Pt).y + rngRange(rng, -900, 900);
        const r = worldToHex(0, y).r;
        stones.push({ x: (west[r + R] as number) + 1500, y });
      }
    const puddles: Pt[] = [];
    for (let k = 0; k < (kind === 'brink' ? 10 : 16); k++) {
      const p = { x: rngRange(rng, -R + 2, R - 2) * 900, y: rngRange(rng, -R + 2, R - 2) * 800 };
      if (onGround(ground, p)) puddles.push(p);
    }
    const n0 = nodes[0] as Pt;
    return { kind, radius: R, rocks, west, east, crossings, end: { ...(nodes[n] as Pt) }, start: { x: n0.x, y: n0.y + 1400 }, stones, puddles };
  }
};

/** A clue's point on a branch (-1: none). */
const clueAt = (b: TrailBranch, i: number): Pt | null => (i >= 0 ? (b.pts[i] as Pt) : null);

/** What a branch shows within `see` of a point: the pack's stops (a jump, a drink) and the Wolf's pit. */
export const branchSeen = (b: TrailBranch, from: Pt, see: number): { pack: boolean; pit: boolean } => {
  const near = (p: Pt | null): boolean => !!p && dist(p, from) <= see;
  return { pack: (b.jump && near(clueAt(b, b.stream))) || (b.drink && near(clueAt(b, b.puddle))), pit: near(clueAt(b, b.pit)) };
};

/**
 * The check: in every crossing one branch only (the Wolf's) keeps all three rules (it neither jumps nor drinks); each
 * of the pack's breaks one. A late crossing shows nothing within sight of its middle; any other can be read from
 * there (every pack's branch shows its stop, or the Wolf's its pit). Act 3 has three late crossings.
 */
export const trailCheck = (L: { crossings: TrailCrossing[]; see: number }): boolean => {
  let late = 0;
  for (const c of L.crossings) {
    const keep = c.branches.filter((b) => !b.jump && !b.drink);
    if (keep.length !== 1 || !keep[0]?.right) return false;
    if (c.branches.some((b) => !b.right && b.stream < 0 && b.puddle < 0)) return false;
    const seen = c.branches.map((b) => branchSeen(b, c.at, L.see));
    if (c.late) {
      late++;
      if (seen.some((x) => x.pack || x.pit)) return false;
    } else {
      const packAll = c.branches.every((b, i) => b.right || (seen[i] as { pack: boolean }).pack);
      const pit = (seen[c.right] as { pit: boolean }).pit;
      if (!packAll && !pit) return false;
    }
  }
  return L.crossings.length !== CAZA.trail.brink.crossings || late === 3;
};
